import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import CreateQuotationForm from "../components/CreateQuotationForm";
import ItemsForm from "../components/ItemsForm";
import QuotationPreview from "../components/QuotationPreview";
import ApprovalFlowHorizontal from "../components/ApprovalFlowHorizontal";
import LoadingOverlay from "../components/LoadingOverlay";
import BackButton from "../components/BackButton";
import { pdf } from "@react-pdf/renderer"; // สำหรับการสร้าง Blob
import bankAccounts from "../data/bankAccounts.json"; // โหลดข้อมูลธนาคาร
import { apiURL, COMPANY_PREFIX } from "../config/config";
import { ClipLoader } from "react-spinners"; // ✅ ใช้ react-spinners
import { toast } from "react-toastify"; // ✅ ใช้ react-toastify
import "react-toastify/dist/ReactToastify.css"; // ✅ Import styles
import _ from "lodash";
import { upsertCachedQuotation } from "../utils/quotationCache";

// ✅ ฟังก์ชันปัดเศษให้เป็นทศนิยม 2 ตำแหน่ง (ปัดขึ้นถ้าหลักที่ 3 >= 5)
const roundUp = (num) => {
  return (num * 100) % 1 >= 0.5 ? _.ceil(num, 2) : _.round(num, 2);
};

const requiredFields = [
  ["title", "Title"],
  ["clientId", "Client"],
  ["salePerson", "Create By"],
  ["documentDate", "Document Date"],
  ["productName", "Product Name"],
  ["startDate", "Start Date"],
  ["endDate", "End Date"],
  ["type", "Type"],
];

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const EditQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quotationData, setQuotationData] = useState({
    title: "",
    client: "",
    salePerson: "",
    documentDate: "",
    productName: "",
    projectName: "",
    period: "",
    startDate: "",
    endDate: "",
    createBy: "",
    proposedBy: "",
    type: "M",
    fee: 0,
    items: [],
    discount: 0,
    createdByUser: "",
    totalBeforeFee: 0,
    netAmount: 0,
    remark: "",
  });

  const [item, setItem] = useState({ description: "", unit: 1, unitPrice: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false); // ✅ Loading สำหรับบันทึกข้อมูล
  const [isDetailedForm, setIsDetailedForm] = useState(false); // ✅ Default เป็น Standard Form
  const [isSpecialForm, setIsSpecialForm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [itemErrors, setItemErrors] = useState({});

  // โหลดข้อมูลจาก API
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: "smooth" });
    const fetchQuotation = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiURL}quotations/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data;

        // Pending quotations without a flow must confirm one; Drafts remain editable.
        if (
          data.approvalStatus !== "Draft" &&
          (!data.approvalHierarchy || data.approvalHierarchy.length === 0)
        ) {
          navigate(`/request-approve-flow/${id}`);
          return;
        }

        const formatDate = (isoDate) => (isoDate ? isoDate.split("T")[0] : "");
        setQuotationData({
          ...data,
          clientId: data.clientId?._id || "",
          client: data.clientId?.customerName || "",
          documentDate: formatDate(data.documentDate),
          startDate: formatDate(data.startDate),
          endDate: formatDate(data.endDate),
          items: data.items || [],
          totalBeforeFee: data.totalBeforeFee || 0,
          netAmount: data.netAmount || 0,
        });
        setIsDetailedForm(data.isDetailedForm || false); // ✅ โหลดค่า isDetailedForm จากฐานข้อมูล
        setIsSpecialForm(data.isSpecialForm || false);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching quotation:", err);
        toast.error("Failed to load quotation ❌");
        setLoading(false);
      }
    };

    fetchQuotation();
  }, [id, navigate]);
  // คำนวณ Total และ Net Amount
  useEffect(() => {
    const totalBeforeFee = roundUp(
      quotationData.items.reduce(
        (sum, itm) => sum + itm.unit * roundUp(itm.unitPrice),
        0
      )
    );
    const calFee = roundUp(quotationData.fee); // ✅ ใช้ค่าที่ส่งมาจากฟอร์ม (จำนวนเงิน)
    const amountBeforeTax = roundUp(
      totalBeforeFee + calFee - quotationData.discount
    );
    const vat = roundUp(amountBeforeTax * 0.07);
    const netAmount = roundUp(amountBeforeTax + vat);

    setQuotationData((prev) => ({
      ...prev,
      totalBeforeFee,
      netAmount,
      amountBeforeTax,
      vat,
    }));
  }, [quotationData.items, quotationData.discount, quotationData.fee]);

  const handleItemChange = (field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setItemErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const addItem = () => {
    const nextItemErrors = {};
    if (isBlank(item.description)) {
      nextItemErrors.description = "Item description is required";
    }
    if (isBlank(item.unit) || Number(item.unit) <= 0) {
      nextItemErrors.unit = "Units must be greater than 0";
    }
    if (isBlank(item.unitPrice) || Number(item.unitPrice) <= 0) {
      nextItemErrors.unitPrice = "Unit price must be greater than 0";
    }

    if (Object.keys(nextItemErrors).length > 0) {
      setItemErrors(nextItemErrors);
      toast.error("กรุณากรอกข้อมูลให้ครบทุกช่อง ❌");
      return;
    }

    const calculatedAmount = item.unit * parseFloat(item.unitPrice);
    setQuotationData((prev) => ({
      ...prev,
      items: [...prev.items, { ...item, amount: calculatedAmount || 0 }],
    }));
    setItem({ description: "", unit: 1, unitPrice: "" });
    setItemErrors({});
    setFormErrors((prev) => {
      if (!prev.items) return prev;
      const next = { ...prev };
      delete next.items;
      return next;
    });
  };

  const removeItem = (index) => {
    setQuotationData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateItem = (index, updatedItem) => {
    const calculatedAmount =
      updatedItem.unit * parseFloat(updatedItem.unitPrice || 0);
    setQuotationData((prev) => ({
      ...prev,
      items: prev.items.map((itm, i) =>
        i === index ? { ...updatedItem, amount: calculatedAmount } : itm
      ),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuotationData((prev) => ({
      ...prev,
      [name]:
        name === "discount" || name === "fee" ? parseFloat(value) || 0 : value,
    }));
    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    requiredFields.forEach(([field, label]) => {
      if (isBlank(quotationData[field])) {
        nextErrors[field] = `${label} is required`;
      }
    });

    if (
      quotationData.startDate &&
      quotationData.endDate &&
      new Date(quotationData.endDate) < new Date(quotationData.startDate)
    ) {
      nextErrors.endDate = "End Date must be after Start Date";
    }

    if (quotationData.items.length === 0) {
      nextErrors.items = "Please add at least one item";
    }

    setFormErrors(nextErrors);
    return nextErrors;
  };

  const handlePreview = async () => {
    // console.log("quotationData Edit => ", quotationData);
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const company = user.company || "";
      const bankInfo = bankAccounts.companies[company] || {};

      // ✅ ใช้ roundUp() แทน toFixed(2)
      const totalBeforeFee = roundUp(
        quotationData.items.reduce(
          (sum, itm) =>
            sum + (itm.unit || 0) * roundUp(parseFloat(itm.unitPrice) || 0),
          0
        )
      );
      const feeAmount = roundUp(quotationData.fee || 0); // ดึงจาก state ล่าสุด
      const discount = roundUp(quotationData.discount || 0);
      const amountBeforeTax = roundUp(totalBeforeFee + feeAmount - discount);
      const vat = roundUp(amountBeforeTax * 0.07);
      const netAmount = roundUp(amountBeforeTax + vat);

      if (!quotationData.clientId) {
        alert("Please select a client before previewing.");
        return;
      }

      const clientResponse = await axios.get(
        `${apiURL}clients/${quotationData.clientId}`
      );
      const clientDetails = clientResponse.data;

      const total = roundUp(totalBeforeFee + feeAmount);
      const updatedQuotationData = {
        ...quotationData,
        totalBeforeFee,
        feeAmount,
        total, // ✅ ใส่ total คำนวณใหม่
        amountBeforeTax,
        vat,
        netAmount,
        clientDetails,
        isDetailedForm, // ✅ เอาสด ๆ
        isSpecialForm, // ✅ เอาสด ๆ
      };

      const blob = await pdf(
        <QuotationPreview
          quotationData={updatedQuotationData}
          bankInfo={bankInfo}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error generating preview:", error);
      alert("Failed to generate preview.");
    }
  };
  const handleSave = async (e, status) => {
    if (e) e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      toast.error("กรุณากรอกข้อมูลที่จำเป็นให้ครบ");
      return;
    }

    setSaving(true);

    const toISOString = (dateString) =>
      dateString ? new Date(dateString).toISOString() : null;

    const processedItems = quotationData.items.map((item) => ({
      ...item,
      unitPrice: roundUp(item.unitPrice),
      amount: roundUp(item.unit * roundUp(item.unitPrice)),
    }));

    const totalBeforeFee = roundUp(
      processedItems.reduce((sum, itm) => sum + itm.amount, 0)
    );
    const calFee = roundUp(quotationData.fee); // ✅ ตรงตาม requirement ของ Neon (ใช้เป็นจำนวนเงิน)
    const total = roundUp(totalBeforeFee + calFee);
    const amountBeforeTax = roundUp(total - quotationData.discount);
    const vat = roundUp(amountBeforeTax * 0.07);
    const netAmount = roundUp(amountBeforeTax + vat);

    const updatedQuotationData = {
      ...quotationData,
      documentDate: toISOString(quotationData.documentDate),
      startDate: toISOString(quotationData.startDate),
      endDate: toISOString(quotationData.endDate),
      totalBeforeFee,
      fee: roundUp(quotationData.fee),
      calFee,
      total,
      discount: roundUp(quotationData.discount),
      amountBeforeTax,
      vat,
      netAmount,
      items: processedItems,
      isDetailedForm,
      isSpecialForm,
      approvalStatus: status, // ✅ ส่งค่าจากปุ่ม Draft หรือ Pending
    };

    try {
      const token = localStorage.getItem("token");

      // 1. **อัปเดตข้อมูล Quotation**
      await axios.patch(`${apiURL}quotations/${id}`, updatedQuotationData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      upsertCachedQuotation({ ...updatedQuotationData, _id: id });

      // 2. **Reset approvalHierarchy เฉพาะตอนที่ไม่ใช่ Draft**
      if (status !== "Draft" && quotationData.approvalHierarchy?.length > 0) {
        const approvalId = quotationData.approvalHierarchy[0]._id;
        const newApprovalFlow =
          quotationData.approvalHierarchy[0].approvalHierarchy.map((level) => ({
            level: level.level,
            approver: level.approver,
            status: "Pending",
          }));

        await axios.patch(
          `${apiURL}approvals/${approvalId}/reset`,
          { approvalHierarchy: newApprovalFlow },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      toast.success(
        status === "Draft"
          ? "Draft saved successfully!"
          : status === "Pending"
          ? "Sent to Pending successfully!"
          : "Quotation updated successfully!"
      );

      if (status !== "Draft") {
        if (!quotationData.approvalHierarchy?.length) {
          navigate(`/request-approve-flow/${id}`);
        } else {
          navigate(`/quotations/${id}`);
        }
      }
    } catch (err) {
      console.error("Error saving quotation:", err);
      toast.error("Failed to save quotation ❌");
    } finally {
      setSaving(false);
    }
  };

  const handleClientChange = (clientId, clientName) => {
    setQuotationData((prev) => ({
      ...prev,
      clientId, // อัปเดต clientId
      client: clientName, // อัปเดต client name
    }));
    setFormErrors((prev) => {
      if (!prev.clientId) return prev;
      const next = { ...prev };
      delete next.clientId;
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white bg-opacity-80">
        <ClipLoader color="#6366F1" loading={loading} size={60} />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-5 relative bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      {/* ✅ Loading Overlay ตอนกำลังบันทึก */}
      <LoadingOverlay loading={saving} />

      {/* Header Section */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-between mb-2">
          <BackButton label="ย้อนกลับ" />
        </div>

        <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 leading-snug">
          กำลังแก้ไขใบเสนอราคา:
          <span className="ml-1 sm:ml-2 text-indigo-600 dark:text-indigo-400 font-semibold block sm:inline mt-1 sm:mt-0">
            {quotationData.runNumber
              ? (() => {
                  const year = new Date(
                    quotationData.documentDate
                  ).getFullYear();
                  const run =
                    quotationData.runNumber?.toString().padStart(3, "0") ||
                    "???";
                  const prefix = COMPANY_PREFIX;
                  return `${prefix}(${
                    quotationData.type || "M"
                  })-${year}-${run}`;
                })()
              : ""}
          </span>
        </h2>

        {(quotationData.approvalStatus === "Rejected" ||
          quotationData.approvalStatus === "Canceled") &&
          quotationData.reason && (
            <div className="mt-3 sm:mt-4 flex items-start gap-2 rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2.5 text-sm sm:text-base text-red-700 dark:text-red-300 shadow-sm transition-colors duration-300">
              <div>
                <span className="font-semibold">
                  {quotationData.approvalStatus === "Rejected"
                    ? "เหตุผลที่ถูกตีกลับ:"
                    : "เหตุผลที่ถูกยกเลิก:"}
                </span>{" "}
                {quotationData.reason}
              </div>
            </div>
          )}
      </div>

      {/* Title */}
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-yellow-300 mb-5 sm:mb-7 transition-colors duration-300">
        Edit Quotation
      </h1>

      {/* ✅ Approval Flow Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 transition-colors duration-300">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 sm:mb-5 flex items-center gap-2">
          <span className="inline-block w-1.5 h-5 bg-indigo-500 rounded-full"></span>
          Approval Flow
        </h2>
        <ApprovalFlowHorizontal
          approvalHierarchy={quotationData.approvalHierarchy || []}
        />
      </div>

      {/* ✅ Form Section */}
      <form
        className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm rounded-xl p-4 sm:p-6 transition-colors duration-300"
        noValidate
      >
        <CreateQuotationForm
          quotationData={quotationData}
          handleChange={handleChange}
          setQuotationData={setQuotationData}
          handleClientChange={handleClientChange}
          errors={formErrors}
        />

        <div className="mt-6 sm:mt-8">
          <ItemsForm
            items={quotationData.items}
            item={item}
            handleItemChange={handleItemChange}
            addItem={addItem}
            removeItem={removeItem}
            updateItem={updateItem}
            isDetailedForm={isDetailedForm}
            setIsDetailedForm={setIsDetailedForm}
            isSpecialForm={isSpecialForm}
            setIsSpecialForm={setIsSpecialForm}
            itemErrors={itemErrors}
            itemsError={formErrors.items}
          />
        </div>

        {/* ✅ Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 mt-8 border-t border-gray-200 dark:border-gray-700 pt-5 transition-colors duration-300">
          {/* Preview */}
          <button
            type="button"
            onClick={handlePreview}
            className="w-full sm:w-auto px-5 py-2.5 rounded-md bg-gray-100 dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
            disabled={saving}
          >
            Preview
          </button>

          {/* Save Draft */}
          {quotationData.approvalStatus === "Draft" && (
            <button
              type="button"
              onClick={() => handleSave(null, "Draft")}
              className="w-full sm:w-auto px-5 py-2.5 rounded-md bg-yellow-100 dark:bg-yellow-900/40 text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-800/60 transition"
              disabled={saving}
            >
              Save Draft
            </button>
          )}

          {/* Save Changes / Send to Approve */}
          <button
            type="button"
            onClick={(e) =>
              handleSave(
                e,
                quotationData.approvalStatus === "Draft"
                  ? "Pending"
                  : quotationData.approvalStatus
              )
            }
            className={`w-full sm:w-auto px-5 py-2.5 rounded-md text-sm font-medium transition
          ${
            saving
              ? "bg-indigo-200 text-indigo-400 cursor-not-allowed"
              : "bg-indigo-500 dark:bg-indigo-600 text-white hover:bg-indigo-600 dark:hover:bg-indigo-700"
          }`}
            disabled={saving}
          >
            {quotationData.approvalStatus === "Draft"
              ? "Send to Approve"
              : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditQuotation;
